import cn from "@/v2-ui/utils/utils.cn"
import Badge, { type BadgeProps } from "@/v2-backoffice-client/components/badge"
import { getLicenseProductIdentity } from "@/v2-backoffice-client/license/product/license.product.utils"

export type LicenseProductBadgeProps = Omit<
  BadgeProps,
  "variant" | "iconSrc" | "children"
> & {
  /** The product's name (lic.Products.Name), which decides the hue and glyph. */
  productName: string | null | undefined
  /** What the pill says; the product's name when omitted. */
  children?: BadgeProps["children"]
  /** Leave the glyph out where the pill must stay narrow. */
  hideIcon?: boolean
}

/**
 * THE product pill: a product's name in its own hue with its own glyph
 * (license.product.utils), identical wherever a product appears — the
 * Licenses board and panel, a license's product column, a customer's
 * license chips, the add-on catalog's "goes with". Use it for every product
 * name shown as a pill; a plain neutral badge is for everything that is not
 * a product.
 */
function LicenseProductBadge(props: LicenseProductBadgeProps) {
  const {
    productName,
    children,
    hideIcon = false,
    className,
    ...badgeProps
  } = props
  const identity = getLicenseProductIdentity(productName)

  return (
    <Badge
      className={cn(
        "license-product-badge",
        `license-product-badge-${identity.tone}`,
        className,
      )}
      iconSrc={hideIcon ? undefined : identity.iconSrc}
      {...badgeProps}
    >
      {children ?? productName}
    </Badge>
  )
}

export default LicenseProductBadge
